import React, { useState, useEffect, useCallback } from 'react'
import './App.css'
import './FeesPage.css' // the Feepage stylesheet (same import as the other pages)
import './Home.css' // shared ticket cards, chips, search and states
import { Link, useLocation } from 'react-router-dom'
import axios from 'axios'
import TopBarProgress from 'react-topbar-progress-indicator'
import { API_URL } from './config'
import { BiSolidLocationPlus } from 'react-icons/bi'
import { BsArrowRight, BsClock, BsSearch, BsTicketPerforated } from 'react-icons/bs'
import Header from './layouts/Header'
import IndexedEvents from './IndexedEvents'

const pad = (n) => String(n).padStart(2, '0')

const todayISO = () => {
  const d = new Date()

  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

const plainText = (html = '', max = 90) => {
  const text = new DOMParser().parseFromString(String(html), 'text/html').body.textContent || ''

  const clean = text.replace(/\s+/g, ' ').trim()

  return clean.length > max ? `${clean.slice(0, max).trimEnd()}…` : clean
}

const posterUrl = (item) =>
  `${API_URL}/apps/kscanner/posters/${item.businessid}/${item.eventid}/${item.poster}`

const hidePoster = (e) => {
  e.currentTarget.style.display = 'none'
}

// Cursor-follow spotlight on cards.
const spot = (e) => {
  const r = e.currentTarget.getBoundingClientRect()
  e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`)
  e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`)
}

const Explore = () => {
  const location = useLocation()

  const [province, setProvince] = useState(location.state?.province || '')

  const [currentEvent, setCurrentEvent] = useState([])
  const [provinces, setProvinces] = useState([])
  const [eventCategory, setEventCategory] = useState([])

  const [userData, setUserData] = useState({
    email: '',
    phone: '',
    customername: '',
  })

  const [query, setQuery] = useState('')
  const [category, setCategory] = useState('')

  const [loading, setLoading] = useState({
    events: true,
    filters: true,
    error: null,
  })

  const customerid = localStorage.getItem('customerid')
  const accounttype = localStorage.getItem('accounttype')

  const fetchEvents = useCallback((prov) => {
    setLoading((prev) => ({
      ...prev,
      events: true,
      error: null,
    }))

    const queryProv = prov ? `&province=${encodeURIComponent(prov)}` : ''

    const apiUrl = `${API_URL}/apps/kscanner/webservices.php` + `?query=EVENTS${queryProv}`

    fetch(apiUrl)
      .then((res) => res.json())
      .then((result) => {
        setCurrentEvent(Array.isArray(result) ? result : [])

        setLoading((prev) => ({
          ...prev,
          events: false,
        }))
      })
      .catch((error) => {
        setLoading((prev) => ({
          ...prev,
          events: false,
          error,
        }))
      })
  }, [])

  useEffect(() => {
    document.title = province ? `${province} - Karamale` : 'Explore - Karamale'

    if (customerid) {
      axios
        .get(
          `${API_URL}/apps/kscanner/webservices.php?query=CUSTOMERDETAILS&accountid=${customerid}`,
          {
            withCredentials: true,
          },
        )
        .then((response) => {
          setUserData({
            email: response.data.email,
            phone: response.data.phone,
            customername: response.data.customername,
          })
        })
        .catch(() => {})
    }

    fetchEvents(province)

    Promise.all([
      axios.get(`${API_URL}/apps/kscanner/webservices.php?query=PROVINCES`),
      axios.get(`${API_URL}/apps/kscanner/webservices.php?query=EVENTCATEGORY&limit=9`),
    ])
      .then(([provinceResponse, categoryResponse]) => {
        setProvinces(Array.isArray(provinceResponse.data) ? provinceResponse.data : [])

        setEventCategory(Array.isArray(categoryResponse.data) ? categoryResponse.data : [])
      })
      .catch(() => {})
      .finally(() => {
        setLoading((prev) => ({
          ...prev,
          filters: false,
        }))
      })
  }, [province, fetchEvents, customerid])

  const handleProvinceChange = (e) => {
    const provinceId = e.target.value

    if (!provinceId) {
      setProvince('')
      setQuery('')
      setCategory('')
      return
    }

    const selected = provinces.find((item) => String(item.provinceid) === provinceId)

    setProvince(selected ? selected.province : '')
    setQuery('')
    setCategory('')
  }

  const clearFilters = () => {
    setQuery('')
    setCategory('')
  }

  const today = todayISO()

  const visibleEvents = currentEvent.filter(
    (item) => !(item.balance < 3.5 && item.event_day > today),
  )

  const searchTerm = query.trim().toLowerCase()

  const shownEvents = visibleEvents.filter((item) => {
    const matchesCategory = !category || item.eventcategory === category

    const searchableText = `
      ${item.event_name || ''}
      ${item.event_location || ''}
    `.toLowerCase()

    const matchesSearch = !searchTerm || searchableText.includes(searchTerm)

    return matchesCategory && matchesSearch
  })

  const filtering = Boolean(searchTerm || category)

  if (loading.error) {
    return (
      <div className="fp-page">
        <Header />

        <section className="fp-section fp-section--last">
          <div className="fp-wrap">
            <div className="fp-state" role="alert">
              <BsTicketPerforated className="fp-state__icon" aria-hidden="true" />
              <h3>We couldn't load events</h3>
              <p>Check your connection and try again.</p>
              <div className="fp-actions fp-actions--center">
                <button
                  type="button"
                  className="fp-btn fp-btn--brand"
                  onClick={() => fetchEvents(province)}
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    )
  }

  return (
    <div className="fp-page">
      <Header />

      {loading.filters && <TopBarProgress />}

      <main>
        {/* ---------- Heading, search and filters ---------- */}
        <section className="fp-hero fp-hero--slim">
          <div className="fp-aurora" aria-hidden="true">
            <span className="fp-aurora__a" />
            <span className="fp-aurora__b" />
          </div>

          <div className="fp-wrap">
            <span className="fp-pill">
              <i />
              {province || 'All provinces'}
            </span>

            <h1>
              Explore <em>events.</em>
            </h1>

            <p className="fp-hero__sub">
              Discover concerts, festivals and experiences{' '}
              {province ? (
                <>
                  happening in <strong>{province}</strong>.
                </>
              ) : (
                'happening around you.'
              )}
            </p>

            {!loading.events && (
              <span className="fp-count">
                {shownEvents.length} {shownEvents.length === 1 ? 'event' : 'events'}
              </span>
            )}

            <div className="fp-search fp-search--explore">
              <label className="fp-search__field">
                <BsSearch aria-hidden="true" />

                <input
                  type="search"
                  placeholder="Search events or venues"
                  aria-label="Search events or venues"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </label>

              <label className="fp-search__field fp-search__field--select">
                <BiSolidLocationPlus aria-hidden="true" />

                <select
                  aria-label="Browse by province"
                  value={provinces.find((p) => p.province === province)?.provinceid || ''}
                  onChange={handleProvinceChange}
                >
                  <option value="">All provinces</option>

                  {provinces.map((p) => (
                    <option key={p.provinceid} value={p.provinceid}>
                      {p.province}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {eventCategory.length > 0 && (
              <div
                className="fp-chips fp-chips--tight"
                role="group"
                aria-label="Filter by category"
              >
                <button
                  type="button"
                  className={`fp-chip ${!category ? 'is-active' : ''}`}
                  aria-pressed={!category}
                  onClick={() => setCategory('')}
                >
                  All
                </button>

                {eventCategory.map((item) => (
                  <button
                    key={item.category}
                    type="button"
                    className={`fp-chip ${category === item.category ? 'is-active' : ''}`}
                    aria-pressed={category === item.category}
                    onClick={() => setCategory(item.category)}
                  >
                    {item.category}
                  </button>
                ))}

                <Link to="/categories" className="fp-chip fp-chip--dashed">
                  More categories
                </Link>
              </div>
            )}
          </div>
        </section>

        {/* ---------- Karamale events ---------- */}
        <section className="fp-section">
          <div className="fp-wrap">
            {loading.events && (
              <div className="fp-egrid" aria-busy="true" aria-label="Loading events">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div className="fp-skel" key={i}>
                    <div className="fp-skel__img" />

                    <div className="fp-skel__body">
                      <div className="fp-skel__line" />
                      <div className="fp-skel__line" />
                      <div className="fp-skel__line" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!loading.events && shownEvents.length === 0 && (
              <div className="fp-state">
                <BsTicketPerforated className="fp-state__icon" aria-hidden="true" />

                <h3>{filtering ? 'No events match your search' : 'No events found'}</h3>

                <p>
                  {filtering
                    ? 'Try a different name, province or category.'
                    : 'New events are added often. Check back soon.'}
                </p>

                {filtering && (
                  <div className="fp-actions fp-actions--center">
                    <button type="button" className="fp-btn fp-btn--brand" onClick={clearFilters}>
                      Clear filters
                    </button>
                  </div>
                )}
              </div>
            )}

            {!loading.events && shownEvents.length > 0 && (
              <div className="fp-egrid">
                {shownEvents.map((item, index) => {
                  const posterUri = posterUrl(item)

                  return (
                    <div className="fp-cell" key={item.eventid || index}>
                      <Link
                        to={{
                          pathname: `/event/${item.eventid}/tickets`,
                          state: {
                            fromHome: true,
                            eventid: item.eventid,
                            eventDate: item.event_day,
                            fulldate: today,
                            customerid,
                            posterUri,
                            accountid: customerid,
                            accounttype,
                            customername: userData.customername,
                            email: userData.email,
                            phone: userData.phone,
                            venue: item.event_location,
                            description: item.event_des,
                            businessid: item.businessid,
                            poster: item.poster,
                            eventname: item.event_name,
                            eventday: item.eventday,
                            starttime: item.start_time,
                            endtime: item.finish_time,
                            previousPage: 'explore',
                            campaignStatus: 'running',
                            category: item.eventcategory,
                          },
                        }}
                        className="fp-event"
                        aria-label={`${item.event_name}, ${item.eventday}`}
                        onMouseMove={spot}
                      >
                        <div className="fp-event__bg">
                          <BsTicketPerforated aria-hidden="true" />
                          <img src={posterUri} alt="" loading="lazy" onError={hidePoster} />
                        </div>
                        <div className="fp-event__shade" aria-hidden="true" />

                        {item.eventcategory && (
                          <span className="fp-event__cat">{item.eventcategory}</span>
                        )}

                        <div className="fp-event__info">
                          <h3 className="fp-event__title">{item.event_name}</h3>

                          <p className="fp-event__place">
                            <BiSolidLocationPlus aria-hidden="true" />
                            <span>{item.event_location}</span>
                          </p>

                          <p className="fp-event__desc">{plainText(item.event_des)}</p>
                        </div>

                        <div className="fp-event__stub">
                          <div className="fp-event__when">
                            <strong>{item.eventday}</strong>

                            {item.start_time && (
                              <span>
                                <BsClock aria-hidden="true" /> {item.start_time}
                              </span>
                            )}
                          </div>

                          <span className="fp-event__cta">
                            Get tickets <BsArrowRight aria-hidden="true" />
                          </span>
                        </div>
                      </Link>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </section>

        {/* ---------- External events ---------- */}
        <section className="fp-section fp-section--last">
          <div className="fp-wrap">
            <IndexedEvents province={province} />
          </div>
        </section>
      </main>
    </div>
  )
}

export default Explore
